/**
 * German messages for react-admin 5's own UI.
 *
 * Vendored rather than installed. The only package for this,
 * ra-language-german, was pulled from a GitHub URL — outside npm and so outside
 * the min-release-age rule — and tracks react-admin 3: 57 of react-admin 5's 164
 * keys were missing, which surfaced as "Missing translation for key" warnings
 * and English text in the German UI. No maintained npm package targets v5.
 *
 * The key set follows ra-language-english 5.x exactly. Strings come from
 * ra-language-german 3.18.0 (commit 5dc19dc) where it had them; the rest are
 * translated here. When upgrading react-admin, diff the key set against
 * ra-language-english and add what is new.
 *
 * Contains material from ra-language-german, used under its licence:
 *
 * MIT License
 *
 * Copyright (c) 2018 GreenBanana GmbH
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import type { TranslationMessages } from 'react-admin'

const germanMessages: TranslationMessages = {
  ra: {
    action: {
      add_filter: 'Filter hinzufügen',
      add: 'Neu',
      back: 'Zurück',
      bulk_actions: 'Ein Element ausgewählt |||| %{smart_count} Elemente ausgewählt',
      cancel: 'Abbrechen',
      clear_array_input: 'Liste leeren',
      clear_input_value: 'Eingabe löschen',
      clone: 'Klonen',
      confirm: 'Bestätigen',
      create: 'Erstellen',
      create_item: '%{item} erstellen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      export: 'Exportieren',
      list: 'Liste',
      refresh: 'Aktualisieren',
      remove_filter: 'Filter entfernen',
      remove_all_filters: 'Alle Filter entfernen',
      remove: 'Entfernen',
      reset: 'Zurücksetzen',
      save: 'Speichern',
      search: 'Suchen',
      search_columns: 'Spalten durchsuchen',
      select_all: 'Alle auswählen',
      select_all_button: 'Alle auswählen',
      select_row: 'Diese Zeile auswählen',
      show: 'Anzeigen',
      sort: 'Sortieren',
      undo: 'Zurücksetzen',
      unselect: 'Abwählen',
      expand: 'Erweitern',
      close: 'Schließen',
      open_menu: 'Menü öffnen',
      close_menu: 'Menü schließen',
      update: 'Aktualisieren',
      move_up: 'Nach oben bewegen',
      move_down: 'Nach unten bewegen',
      open: 'Öffnen',
      toggle_theme: 'Hell/Dunkel umschalten',
      select_columns: 'Spalten',
      update_application: 'Anwendung neu laden',
    },
    boolean: {
      true: 'Ja',
      false: 'Nein',
      null: ' ',
    },
    page: {
      create: '%{name} erstellen',
      dashboard: 'Dashboard',
      edit: '%{name} #%{id}',
      error: 'Etwas ist schief gelaufen',
      list: '%{name}',
      loading: 'Loading...',
      not_found: 'Nicht gefunden',
      show: '%{name} #%{id}',
      empty: 'Noch kein %{name}.',
      invite: 'Neu erstellen?',
      access_denied: 'Zugriff verweigert',
      authentication_error: 'Authentifizierungsfehler',
    },
    input: {
      file: {
        upload_several:
          'Zum Hochladen Dateien hineinziehen oder hier klicken, um Dateien auszuwählen.',
        upload_single:
          'Zum Hochladen Datei hineinziehen oder hier klicken, um eine Datei auszuwählen.',
      },
      image: {
        upload_several:
          'Zum Hochladen Bilder hineinziehen oder hier klicken, um Bilder auszuwählen.',
        upload_single:
          'Zum Hochladen Bild hineinziehen oder hier klicken, um ein Bild auszuwählen.',
      },
      references: {
        all_missing: 'Die zugehörigen Referenzen konnten nicht gefunden werden.',
        many_missing:
          'Mindestens eine der zugehörigen Referenzen scheint nicht mehr verfügbar zu sein.',
        single_missing: 'Eine zugehörige Referenz scheint nicht mehr verfügbar zu sein.',
      },
      password: {
        toggle_visible: 'Passwort ausblenden',
        toggle_hidden: 'Passwort einblenden',
      },
    },
    message: {
      about: 'Über',
      access_denied: 'Sie haben nicht die erforderlichen Rechte, um diese Seite aufzurufen.',
      are_you_sure: 'Sind Sie sicher?',
      authentication_error:
        'Der Authentifizierungsserver hat einen Fehler gemeldet; Ihre Anmeldedaten konnten nicht geprüft werden.',
      auth_error: 'Beim Prüfen des Anmeldetokens ist ein Fehler aufgetreten.',
      bulk_delete_content:
        '"%{name}" wirklich löschen? |||| %{smart_count} Elemente wirklich löschen?',
      bulk_delete_title: 'Lösche %{name} |||| Lösche %{smart_count} %{name} Elemente',
      bulk_update_content:
        '"%{name}" wirklich aktualisieren? |||| %{smart_count} Elemente wirklich aktualisieren?',
      bulk_update_title: 'Aktualisiere %{name} |||| Aktualisiere %{smart_count} %{name} Elemente',
      clear_array_input: 'Möchten Sie wirklich die gesamte Liste leeren?',
      delete_content: 'Diesen Inhalt wirklich löschen?',
      delete_title: 'Lösche %{name} #%{id}',
      details: 'Details',
      error: 'Ein Fehler ist aufgetreten und die Anfrage konnte nicht abgeschlossen werden.',
      invalid_form: 'Das Formular ist ungültig. Bitte Eingaben überprüfen.',
      loading: 'Die Seite wird geladen, bitte einen Moment Geduld.',
      no: 'Nein',
      not_found: 'Die Seite konnte nicht gefunden werden.',
      select_all_limit_reached:
        'Es gibt zu viele Einträge, um alle auszuwählen. Nur die ersten %{max} wurden ausgewählt.',
      unsaved_changes:
        'Einige Änderungen wurden nicht gespeichert. Diese Seite wirklich verlassen?',
      yes: 'Ja',
      placeholder_data_warning: 'Netzwerkproblem: Die Daten konnten nicht aktualisiert werden.',
    },
    navigation: {
      clear_filters: 'Filter zurücksetzen',
      no_filtered_results: 'Keine %{name} mit den aktuellen Filtern gefunden.',
      no_results: 'Keine Resultate gefunden',
      no_more_results: 'Die Seite %{page} enthält keine Inhalte.',
      page_out_of_boundaries: 'Die Seite %{page} liegt ausserhalb des gültigen Bereichs',
      page_out_from_end: 'Letzte Seite',
      page_out_from_begin: 'Erste Seite',
      page_range_info: '%{offsetBegin}-%{offsetEnd} von %{total}',
      partial_page_range_info: '%{offsetBegin}-%{offsetEnd} von mehr als %{offsetEnd}',
      current_page: 'Seite %{page}',
      page: 'Zu Seite %{page}',
      first: 'Zur ersten Seite',
      last: 'Zur letzten Seite',
      next: 'Weiter',
      previous: 'Zur vorherigen Seite',
      page_rows_per_page: 'Zeilen pro Seite:',
      skip_nav: 'Zum Inhalt springen',
    },
    sort: {
      sort_by: 'Sortieren nach %{field} %{order}',
      ASC: 'aufsteigend',
      DESC: 'absteigend',
    },
    auth: {
      auth_check_error: 'Bitte verbinden um fortzufahren',
      user_menu: 'Profil',
      username: 'Nutzername',
      password: 'Passwort',
      email: 'E-Mail',
      sign_in: 'Anmelden',
      sign_in_error: 'Fehler bei der Anmeldung',
      logout: 'Abmelden',
    },
    notification: {
      updated: 'Element wurde aktualisiert |||| %{smart_count} Elemente wurden aktualisiert',
      created: 'Element wurde erstellt',
      deleted: 'Element wurde gelöscht |||| %{smart_count} Elemente wurden gelöscht',
      bad_item: 'Fehlerhaftes Element',
      item_doesnt_exist: 'Das Element existiert nicht',
      http_error: 'Fehler beim Kommunizieren mit dem Server',
      data_provider_error: 'Fehler im dataProvider. Prüfe die Konsole für Details.',
      i18n_error: 'Die Übersetzungen für die angegebene Sprache können nicht geladen werden.',
      canceled: 'Aktion abgebrochen',
      logged_out: 'Die Sitzung wurde beendet, bitte erneut verbinden.',
      not_authorized: 'Nicht berechtigt, auf diese Ressource zuzugreifen.',
      application_update_available: 'Eine neue Version ist verfügbar.',
      offline: 'Keine Verbindung. Daten konnten nicht geladen werden.',
    },
    validation: {
      required: 'Benötigt',
      minLength: 'Muss mindestens %{min} Zeichen lang sein',
      maxLength: 'Darf maximal %{max} Zeichen lang sein',
      minValue: 'Muss mindestens %{min} sein',
      maxValue: 'Muss %{max} oder weniger sein',
      number: 'Muss eine Nummer sein',
      email: 'Muss eine gültige E-Mail sein',
      oneOf: 'Es muss einer sein von: %{options}',
      regex: 'Es muss folgendem regulären Ausdruck entsprechen: %{pattern}',
      unique: 'Muss eindeutig sein',
    },
    saved_queries: {
      label: 'Gespeicherte Abfragen',
      query_name: 'Name der Abfrage',
      new_label: 'Aktuelle Abfrage speichern …',
      new_dialog_title: 'Aktuelle Abfrage speichern als',
      remove_label: 'Gespeicherte Abfrage entfernen',
      remove_label_with_name: 'Abfrage „%{name}“ entfernen',
      remove_dialog_title: 'Gespeicherte Abfrage entfernen?',
      remove_message:
        'Möchten Sie diesen Eintrag wirklich aus Ihren gespeicherten Abfragen entfernen?',
      help: 'Filtern Sie die Liste und speichern Sie die Abfrage für später.',
    },
    guesser: {
      empty: {
        title: 'Keine Daten zum Anzeigen',
        message: 'Bitte prüfen Sie Ihren Data Provider.',
      },
    },
    configurable: {
      customize: 'Anpassen',
      configureMode: 'Diese Seite konfigurieren',
      inspector: {
        title: 'Inspektor',
        content: 'Fahren Sie über Elemente der Oberfläche, um sie zu konfigurieren',
        reset: 'Einstellungen zurücksetzen',
        hideAll: 'Alle ausblenden',
        showAll: 'Alle einblenden',
      },
      Datagrid: {
        title: 'Tabelle',
        unlabeled: 'Spalte ohne Bezeichnung #%{column}',
      },
      SimpleForm: {
        title: 'Formular',
        unlabeled: 'Eingabe ohne Bezeichnung #%{input}',
      },
      SimpleList: {
        title: 'Liste',
        primaryText: 'Primärtext',
        secondaryText: 'Sekundärtext',
        tertiaryText: 'Tertiärtext',
      },
    },
  },
}

export default germanMessages
